'use client';

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import type { Address } from 'viem';
import { ZERO } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { Avatar } from '@morpho-org/ui-v6/client';
import { CardText, Tag } from '@morpho-org/ui-v6/server';
import { formatters, getMarketName, getMarketNameParts } from '@morpho-org/ui-v6/utils';
import { Separator, Tooltip, TooltipContent, TooltipTrigger, TruncatedTooltip } from '@repo/ui';
import { abbreviateAddress } from '@repo/utils';
import type { InKindRedemptionMarket, InKindRedemptionPreview } from '@/components/VaultPage/VaultV2/VaultV2SimpleActionPanel/VaultV2OperationPanel/VaultV2ReviewDialog/inKindRedemption.helpers';
import InKindRedemptionPositionExplanation, { WALLET_LEG_CEILING_NOTE, WALLET_LEG_NOTE, WALLET_LEG_REALISED_NOTE } from '@/components/common/InKindRedemptionPositionExplanation';
import { useInKindRedemptionOutcome } from '@/components/VaultPage/VaultV2/VaultV2SimpleActionPanel/VaultV2OperationPanel/VaultV2ReviewDialog/useInKindRedemptionOutcome';
import { formatTokenAmount } from '@/helpers/formatters';
import type { VaultV2RecapSource } from './hooks/useVaultV2RecapData';
import { useVaultV2RecapFormatting } from './hooks/useVaultV2RecapFormatting';
import RecapApyRow from './RecapApyRow';
import { RecapContainer } from './RecapContainer';
import RecapInteraction from './RecapInteraction';
import RecapLabeledRow from './RecapLabeledRow';
import { RecapApyRowSkeleton, RecapInteractionSkeleton, RecapPositionRowSkeleton, RecapTextSkeleton, RecapVaultHeaderSkeleton } from './RecapSkeletons';
import RecapVaultHeader from './RecapVaultHeader';
export interface VaultV2InKindRedemptionRecapProps {
  vaultAddress: Address;
  preview: InKindRedemptionPreview;
  initialPosition: bigint;
  finalPosition: bigint | undefined;
  market: InKindRedemptionMarket;
  /** Force-deallocate penalty rate (WAD) the preview was priced with. */
  penalty: bigint;
  /**
   * Decode the confirmed receipt and show what the redemption actually produced.
   * Only the confirm dialog opts in: the review dialog renders the same recap
   * before anything is signed, where the only honest numbers are the worst case.
   */
  showRealisedAmounts?: boolean;
  source?: VaultV2RecapSource;
}

/**
 * In-kind redemption recap (VRM-5347), shared by the review and confirm
 * dialogs.
 *
 * Before the transaction lands there is nothing to decode, so the recap shows the chosen
 * Market's preview — the split the bundle is built to execute. The idle leg is a ceiling
 * rather than a promise, since another withdrawal landing first leaves less cash and more
 * Market position, so it can only move one way and the tooltip says so.
 *
 * Under `showRealisedAmounts` — the confirm dialog only — the same rows switch to
 * the amounts decoded from the receipt once it lands. The receipt comes off the
 * transaction context, which is global: the review dialog opts out rather than
 * reasoning about whose transaction it is, since a finished redemption can outlive
 * its dialog (the transaction context is app-global and outlives any single dialog).
 *
 * A receipt that lands and does not decode as a redemption replaces the rows entirely rather
 * than falling back to the preview: see `useInKindRedemptionOutcome`.
 */
const VaultV2InKindRedemptionRecap = t0 => {
  const $ = _c(126);
  const {
    vaultAddress,
    preview,
    initialPosition,
    finalPosition,
    market,
    penalty,
    showRealisedAmounts: t1,
    source
  } = t0;
  const showRealisedAmounts = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== finalPosition || $[1] !== initialPosition || $[2] !== source || $[3] !== vaultAddress) {
    t2 = {
      vaultAddress,
      initialPosition,
      finalPosition,
      source
    };
    $[0] = finalPosition;
    $[1] = initialPosition;
    $[2] = source;
    $[3] = vaultAddress;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const formatting = useVaultV2RecapFormatting(t2);
  const {
    vaultV2,
    vaultAsset,
    formattedInitialPosition,
    detailedFormattedInitialPosition,
    formattedFinalPosition,
    detailedFormattedFinalPosition,
    loading
  } = formatting;
  const t3 = vaultAsset?.address;
  let t4;
  if ($[5] !== preview.marketParams.id || $[6] !== showRealisedAmounts || $[7] !== t3 || $[8] !== vaultAddress) {
    t4 = {
      vaultAddress,
      marketId: preview.marketParams.id,
      assetAddress: t3,
      enabled: showRealisedAmounts
    };
    $[5] = preview.marketParams.id;
    $[6] = showRealisedAmounts;
    $[7] = t3;
    $[8] = vaultAddress;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const outcome = useInKindRedemptionOutcome(t4);
  const {
    exitAssets
  } = preview;
  let t5;
  if ($[10] !== market.collateralAsset || $[11] !== market.loanAsset) {
    t5 = getMarketName({
      collateralAsset: market.collateralAsset,
      loanAsset: market.loanAsset,
      maxLength: 20
    });
    $[10] = market.collateralAsset;
    $[11] = market.loanAsset;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const marketName = t5;
  let t6;
  if ($[13] !== market.collateralAsset || $[14] !== market.loanAsset || $[15] !== preview.marketParams.lltv) {
    t6 = getMarketNameParts({
      collateralAsset: market.collateralAsset,
      loanAsset: market.loanAsset,
      lltv: preview.marketParams.lltv
    });
    $[13] = market.collateralAsset;
    $[14] = market.loanAsset;
    $[15] = preview.marketParams.lltv;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const {
    loanSymbol,
    collateralSymbol
  } = t6;
  let t7;
  if ($[17] !== preview.marketParams.lltv) {
    t7 = formatters.percent.digits(2).unit("%").of(preview.marketParams.lltv, 18);
    $[17] = preview.marketParams.lltv;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t7) {
    t8 = <Tag>{t7}</Tag>;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const lltvTag = t8;
  let t9;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <span className="text-secondary-foreground text-xs">Supplying to</span>;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== market.loanAsset.logoURI) {
    t10 = <Avatar aria-hidden={true} src={market.loanAsset.logoURI} size="small" />;
    $[22] = market.loanAsset.logoURI;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== loanSymbol) {
    t11 = ref => <span ref={ref} className="text-foreground min-w-0 truncate text-xs">{loanSymbol}</span>;
    $[24] = loanSymbol;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] !== loanSymbol || $[27] !== t11) {
    t12 = <TruncatedTooltip text={loanSymbol}>{t11}</TruncatedTooltip>;
    $[26] = loanSymbol;
    $[27] = t11;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t10 || $[30] !== t12) {
    t13 = <span className="gap-2xs inline-flex min-w-0 items-center">{t10}{t12}</span>;
    $[29] = t10;
    $[30] = t12;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== collateralSymbol || $[33] !== lltvTag || $[34] !== market.collateralAsset) {
    t14 = market.collateralAsset ? <span className="gap-2xs inline-flex min-w-0 items-center"><span aria-hidden={true} className="bg-muted-foreground h-sm w-px shrink-0 rounded-full" /><span className="sr-only">backed by</span><span className="gap-2xs inline-flex min-w-0 items-center"><Avatar aria-hidden={true} src={market.collateralAsset.logoURI} size="small" /><TruncatedTooltip text={collateralSymbol ?? ""}>{ref_0 => <span ref={ref_0} className="text-foreground min-w-0 truncate text-xs">{collateralSymbol}</span>}</TruncatedTooltip>{lltvTag}</span></span> : lltvTag;
    $[32] = collateralSymbol;
    $[33] = lltvTag;
    $[34] = market.collateralAsset;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== t13 || $[37] !== t14) {
    t15 = <div className="gap-xs flex flex-col items-stretch sm:flex-row sm:items-center sm:justify-between">{t9}<div className="gap-2xs flex min-w-0 flex-wrap items-center">{t13}{t14}</div></div>;
    $[36] = t13;
    $[37] = t14;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  const supplyingTo = t15;
  if (loading) {
    const t16 = preview.idleAssets > ZERO;
    let t17;
    if ($[39] !== marketName || $[40] !== supplyingTo || $[41] !== t16) {
      t17 = <InKindRedemptionRecapSkeleton supplyingTo={supplyingTo} marketName={marketName} hasWalletLeg={t16} />;
      $[39] = marketName;
      $[40] = supplyingTo;
      $[41] = t16;
      $[42] = t17;
    } else {
      t17 = $[42];
    }
    return t17;
  }
  if (!isDefined(vaultV2) || !isDefined(vaultAsset)) {
    let t16;
    if ($[43] !== vaultAddress) {
      t16 = abbreviateAddress(vaultAddress);
      $[43] = vaultAddress;
      $[44] = t16;
    } else {
      t16 = $[44];
    }
    const t17 = `Cannot display interaction with vault ${t16}`;
    let t18;
    if ($[45] !== t17) {
      t18 = <CardText height="240px" text={t17} />;
      $[45] = t17;
      $[46] = t18;
    } else {
      t18 = $[46];
    }
    return t18;
  }
  if (outcome.kind === "unrecognised") {
    let t16;
    if ($[47] !== vaultV2.image || $[48] !== vaultV2.name) {
      t16 = <RecapVaultHeader image={vaultV2.image} name={vaultV2.name} />;
      $[47] = vaultV2.image;
      $[48] = vaultV2.name;
      $[49] = t16;
    } else {
      t16 = $[49];
    }
    let t17;
    if ($[50] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = <span className="text-secondary-foreground text-xs" data-testid="ikr-not-completed">This transaction did not complete as the selected redemption. Check your Portfolio for your current positions.</span>;
      $[50] = t17;
    } else {
      t17 = $[50];
    }
    let t18;
    if ($[51] !== t16) {
      t18 = <RecapContainer className="gap-lg flex-col">{t16}{t17}</RecapContainer>;
      $[51] = t16;
      $[52] = t18;
    } else {
      t18 = $[52];
    }
    return t18;
  }
  const realised = outcome.kind === "redeemed" ? outcome.amounts : undefined;
  const withdrawnAssets = realised?.exitAssets ?? exitAssets;
  const penaltyAssets = realised?.feeAssets ?? preview.feeAssets;
  const marketAssets = realised?.netAssets ?? preview.netAssets;
  const idleAssets = realised?.idleAssets ?? preview.idleAssets;
  const hasWalletLeg = idleAssets > ZERO;
  let t16;
  if ($[53] !== vaultV2.image || $[54] !== vaultV2.name) {
    t16 = <RecapVaultHeader image={vaultV2.image} name={vaultV2.name} />;
    $[53] = vaultV2.image;
    $[54] = vaultV2.name;
    $[55] = t16;
  } else {
    t16 = $[55];
  }
  let t17;
  if ($[56] !== vaultAsset || $[57] !== withdrawnAssets) {
    t17 = <RecapInteraction amount={withdrawnAssets} asset={vaultAsset} label="Withdraw" />;
    $[56] = vaultAsset;
    $[57] = withdrawnAssets;
    $[58] = t17;
  } else {
    t17 = $[58];
  }
  let t18;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <Separator />;
    $[59] = t18;
  } else {
    t18 = $[59];
  }
  let t19;
  if ($[60] !== vaultAsset.symbol) {
    t19 = <span className="text-secondary-foreground text-xs">Deposit ({vaultAsset.symbol})</span>;
    $[60] = vaultAsset.symbol;
    $[61] = t19;
  } else {
    t19 = $[61];
  }
  let t20;
  if ($[62] !== formattedInitialPosition) {
    t20 = <TooltipTrigger asChild={true}><span className="text-muted-foreground text-xs">{formattedInitialPosition}</span></TooltipTrigger>;
    $[62] = formattedInitialPosition;
    $[63] = t20;
  } else {
    t20 = $[63];
  }
  let t21;
  if ($[64] !== detailedFormattedInitialPosition) {
    t21 = !!detailedFormattedInitialPosition && <TooltipContent>{detailedFormattedInitialPosition}</TooltipContent>;
    $[64] = detailedFormattedInitialPosition;
    $[65] = t21;
  } else {
    t21 = $[65];
  }
  let t22;
  if ($[66] !== t20 || $[67] !== t21) {
    t22 = <Tooltip>{t20}{t21}</Tooltip>;
    $[66] = t20;
    $[67] = t21;
    $[68] = t22;
  } else {
    t22 = $[68];
  }
  let t23;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <span className="text-foreground text-xs">→</span>;
    $[69] = t23;
  } else {
    t23 = $[69];
  }
  let t24;
  if ($[70] !== formattedFinalPosition) {
    t24 = <TooltipTrigger asChild={true}><span className="text-foreground text-xs">{formattedFinalPosition}</span></TooltipTrigger>;
    $[70] = formattedFinalPosition;
    $[71] = t24;
  } else {
    t24 = $[71];
  }
  let t25;
  if ($[72] !== detailedFormattedFinalPosition) {
    t25 = !!detailedFormattedFinalPosition && <TooltipContent>{detailedFormattedFinalPosition}</TooltipContent>;
    $[72] = detailedFormattedFinalPosition;
    $[73] = t25;
  } else {
    t25 = $[73];
  }
  let t26;
  if ($[74] !== t24 || $[75] !== t25) {
    t26 = <Tooltip>{t24}{t25}</Tooltip>;
    $[74] = t24;
    $[75] = t25;
    $[76] = t26;
  } else {
    t26 = $[76];
  }
  let t27;
  if ($[77] !== t22 || $[78] !== t26) {
    t27 = <div className="flex items-center gap-[2px] text-right whitespace-nowrap">{t22}{t23}{t26}</div>;
    $[77] = t22;
    $[78] = t26;
    $[79] = t27;
  } else {
    t27 = $[79];
  }
  let t28;
  if ($[80] !== t19 || $[81] !== t27) {
    t28 = <div className="gap-sm grid grid-cols-[minmax(0,1fr)_auto] items-center">{t19}{t27}</div>;
    $[80] = t19;
    $[81] = t27;
    $[82] = t28;
  } else {
    t28 = $[82];
  }
  let t29;
  if ($[83] !== formatting || $[84] !== vaultAsset) {
    t29 = <RecapApyRow asset={vaultAsset} formatting={formatting} />;
    $[83] = formatting;
    $[84] = vaultAsset;
    $[85] = t29;
  } else {
    t29 = $[85];
  }
  let t30;
  if ($[86] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <Separator />;
    $[86] = t30;
  } else {
    t30 = $[86];
  }
  let t31;
  let t32;
  if ($[87] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = <Separator />;
    t32 = <span className="text-secondary-foreground text-xs">You receive</span>;
    $[87] = t31;
    $[88] = t32;
  } else {
    t31 = $[87];
    t32 = $[88];
  }
  let t33;
  if ($[89] !== hasWalletLeg || $[90] !== idleAssets || $[91] !== realised || $[92] !== vaultAsset) {
    t33 = hasWalletLeg && <RecapLabeledRow label="To your wallet" tooltip={realised ? WALLET_LEG_REALISED_NOTE : <>{WALLET_LEG_NOTE} {WALLET_LEG_CEILING_NOTE}</>} value={realised ? formatTokenAmount(vaultAsset, idleAssets) : `up to ${formatTokenAmount(vaultAsset, idleAssets)}`} />;
    $[89] = hasWalletLeg;
    $[90] = idleAssets;
    $[91] = realised;
    $[92] = vaultAsset;
    $[93] = t33;
  } else {
    t33 = $[93];
  }
  const t34 = `As a ${marketName} position (net of fees)`;
  let t35;
  if ($[94] !== idleAssets || $[95] !== marketAssets || $[96] !== marketName || $[97] !== penalty || $[98] !== penaltyAssets || $[99] !== vaultAsset || $[100] !== withdrawnAssets) {
    t35 = <InKindRedemptionPositionExplanation vaultAsset={vaultAsset} marketName={marketName} withdrawnAssets={withdrawnAssets} penaltyAssets={penaltyAssets} marketAssets={marketAssets} idleAssets={idleAssets} penaltyRate={penalty} />;
    $[94] = idleAssets;
    $[95] = marketAssets;
    $[96] = marketName;
    $[97] = penalty;
    $[98] = penaltyAssets;
    $[99] = vaultAsset;
    $[100] = withdrawnAssets;
    $[101] = t35;
  } else {
    t35 = $[101];
  }
  let t36;
  if ($[102] !== marketAssets || $[103] !== vaultAsset) {
    t36 = formatTokenAmount(vaultAsset, marketAssets);
    $[102] = marketAssets;
    $[103] = vaultAsset;
    $[104] = t36;
  } else {
    t36 = $[104];
  }
  let t37;
  if ($[105] !== t34 || $[106] !== t35 || $[107] !== t36) {
    t37 = <RecapLabeledRow label={t34} tooltip={t35} value={t36} />;
    $[105] = t34;
    $[106] = t35;
    $[107] = t36;
    $[108] = t37;
  } else {
    t37 = $[108];
  }
  let t38;
  if ($[109] !== hasWalletLeg || $[110] !== idleAssets || $[111] !== marketAssets || $[112] !== vaultAsset || $[113] !== withdrawnAssets) {
    t38 = hasWalletLeg && <><Separator /><RecapLabeledRow label="Total" tooltip={<>Everything the redemption hands back, as cash plus a Market position.{" "}{formatTokenAmount(vaultAsset, withdrawnAssets)} leaves your Vault deposit; the difference is the redemption fee.</>} value={formatTokenAmount(vaultAsset, idleAssets + marketAssets)} /></>;
    $[109] = hasWalletLeg;
    $[110] = idleAssets;
    $[111] = marketAssets;
    $[112] = vaultAsset;
    $[113] = withdrawnAssets;
    $[114] = t38;
  } else {
    t38 = $[114];
  }
  let t39;
  if ($[115] !== supplyingTo || $[116] !== t17 || $[117] !== t28 || $[118] !== t29 || $[119] !== t33 || $[120] !== t37 || $[121] !== t38) {
    t39 = <div className="gap-sm flex flex-col items-stretch">{t17}{t18}{t28}{t29}{t30}{supplyingTo}{t31}{t32}{t33}{t37}{t38}</div>;
    $[115] = supplyingTo;
    $[116] = t17;
    $[117] = t28;
    $[118] = t29;
    $[119] = t33;
    $[120] = t37;
    $[121] = t38;
    $[122] = t39;
  } else {
    t39 = $[122];
  }
  let t40;
  if ($[123] !== t16 || $[124] !== t39) {
    t40 = <RecapContainer className="gap-lg flex-col">{t16}{t39}</RecapContainer>;
    $[123] = t16;
    $[124] = t39;
    $[125] = t40;
  } else {
    t40 = $[125];
  }
  return t40;
};
interface InKindRedemptionRecapSkeletonProps {
  supplyingTo: ReactNode;
  marketName: string;
  hasWalletLeg: boolean;
}
const InKindRedemptionRecapSkeleton = t0 => {
  const $ = _c(20);
  const {
    supplyingTo,
    marketName,
    hasWalletLeg
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <RecapVaultHeaderSkeleton />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <RecapInteractionSkeleton label="Withdraw" />;
    t3 = <Separator />;
    t4 = <RecapPositionRowSkeleton />;
    t5 = <RecapApyRowSkeleton />;
    t6 = <Separator />;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
    $[5] = t6;
  } else {
    t2 = $[1];
    t3 = $[2];
    t4 = $[3];
    t5 = $[4];
    t6 = $[5];
  }
  let t7;
  let t8;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Separator />;
    t8 = <span className="text-secondary-foreground text-xs">You receive</span>;
    $[6] = t7;
    $[7] = t8;
  } else {
    t7 = $[6];
    t8 = $[7];
  }
  let t9;
  if ($[8] !== hasWalletLeg) {
    t9 = hasWalletLeg && <RecapLabeledRow label="To your wallet" value={<RecapTextSkeleton />} />;
    $[8] = hasWalletLeg;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  const t10 = `As a ${marketName} position (net of fees)`;
  let t11;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <RecapTextSkeleton />;
    $[10] = t11;
  } else {
    t11 = $[10];
  }
  let t12;
  if ($[11] !== t10) {
    t12 = <RecapLabeledRow label={t10} value={t11} />;
    $[11] = t10;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  let t13;
  if ($[13] !== hasWalletLeg) {
    t13 = hasWalletLeg && <><Separator /><RecapLabeledRow label="Total" value={<RecapTextSkeleton />} /></>;
    $[13] = hasWalletLeg;
    $[14] = t13;
  } else {
    t13 = $[14];
  }
  let t14;
  if ($[15] !== supplyingTo || $[16] !== t12 || $[17] !== t13 || $[18] !== t9) {
    t14 = <RecapContainer className="gap-lg flex-col">{t1}<div className="gap-sm flex flex-col items-stretch">{t2}{t3}{t4}{t5}{t6}{supplyingTo}{t7}{t8}{t9}{t12}{t13}</div></RecapContainer>;
    $[15] = supplyingTo;
    $[16] = t12;
    $[17] = t13;
    $[18] = t9;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  return t14;
};
export default VaultV2InKindRedemptionRecap;